import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout, Spin, Typography, Card, message, Collapse } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { fetchCallInformation, getCallRecording } from '@/api/logs';
import { CallLog } from '@/interfaces/ILogs';
import {
	PhoneOutlined,
	FileTextOutlined,
	MessageOutlined,
	FormOutlined,
	DownOutlined,
} from '@ant-design/icons';
import './CallInformation.scss';
import { CallInformationCard } from './CallInformationCard';
import { CallTranscriptCard } from './CallTranscriptCard';
import { TranscriptMessage } from '@/types/TCallLogs';
import { CallSummaryCard } from './CallSummaryCard';
import { RawTicket } from '../ReceptionistDashboard/FormResponses/types';
import { getTicketsForCall } from '@/api/tickets';
import { CallTicketsCard } from './CallTicketsCard';

const { Content } = Layout;
const { Title, Text } = Typography;

export const CallInformation = () => {
	const { key } = useParams();
	const [callInformation, setCallInformation] = useState<CallLog | null>(null);
	const [audioURl, setAudioURL] = useState<string | null>(null);
	const [audioLoading, setAudioLoading] = useState<boolean>(false);
	const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
	const [tickets, setTickets] = useState<RawTicket[]>([]);

	const {
		mutate: getCallInformation,
		isPending,
		isSuccess,
	} = useMutation({
		mutationKey: ['CallInformation'],
		mutationFn: fetchCallInformation,
		onSuccess: (data) => {
			setCallInformation(data?.data);
		},
	});

	const { mutate: callRecordingMutation } = useMutation({
		mutationKey: ['getCallRecording'],
		mutationFn: getCallRecording,
		onSuccess: (data) => {
			setAudioURL(data.url);
			setAudioLoading(false);
		},
		onError: () => {
			setAudioLoading(false);
		},
	});

	const { mutate: getTicketsMutation, isPending: ticketsLoading } = useMutation({
		mutationKey: ['getTicketsForCall'],
		mutationFn: () => getTicketsForCall(callInformation.username, callInformation.callSid),
		onSuccess: (data) => {
			setTickets(data.tickets || []);
		},
		onError: (error) => {
			message.error(error.message || 'Failed to fetch tickets');
		},
	});

	useEffect(() => {
		if (callInformation?.username && callInformation?.callSid) {
			getTicketsMutation();
		}
	}, [callInformation?.username, callInformation?.callSid, getTicketsMutation]);

	useEffect(() => {
		if (key) {
			const shareableLink = window.decodeURIComponent(key);
			getCallInformation(shareableLink);
		}
	}, [key]);

	useEffect(() => {
		if (callInformation?.isRecorded && callInformation.callSid) {
			setAudioLoading(true);
			callRecordingMutation(callInformation.callSid);
		}
	}, [callInformation?.callSid]);

	const transcriptMessages: TranscriptMessage[] = callInformation?.transcript
		? (callInformation.transcript
				.split('\n')
				.map((message: string, index: number) => {
					if (message.startsWith('assistant@')) {
						return { key: index, sender: 'Assistant', message: message.replace('assistant@', '') };
					} else if (message.startsWith('user@')) {
						return { key: index, sender: 'User', message: message.replace('user@', '') };
					}
					return null;
				})
				.filter(Boolean) as TranscriptMessage[])
		: [];

	const handlePlaybackSpeedChange = (value: number) => {
		setPlaybackSpeed(value);
	};

	const baseClass = 'call-information';

	return (
		<Layout className={`${baseClass} bg-[#bfc9ff]`} data-testid="call-information-layout">
			<div className="bg-white border-b border-gray-200 sticky top-0 z-10" data-testid="header-nav">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex items-center h-16">
						<div className="flex items-center">
							<div className="bg-blue-50 p-2.5 rounded-lg mr-3">
								<PhoneOutlined className="text-xl text-blue-500" />
							</div>
							<Title level={3} className="!mb-0 !text-lg sm:!text-xl">
								Call Information
							</Title>
						</div>
					</div>
				</div>
			</div>

			<Content className="min-h-screen bg-[#F8F9FF] pt-6 pb-8" data-testid="main-content">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					{isPending ? (
						<div className="flex justify-center p-10">
							<Spin size="large" data-testid="loading-spinner" />
						</div>
					) : isSuccess && callInformation ? (
						<div className="space-y-8">
							<Card
								data-testid="call-info-card"
								className="shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl border-0"
								style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
							>
								<CallInformationCard
									callInformation={callInformation as CallLog}
									audioURl={audioURl}
									audioLoading={audioLoading}
									playbackSpeed={playbackSpeed || 1}
									onPlaybackSpeedChange={handlePlaybackSpeedChange}
								/>
							</Card>

							<div className="pb-8">
								<Collapse
									data-testid="collapsible-sections"
									className="bg-transparent border-none space-y-6"
									expandIcon={({ isActive }) => (
										<DownOutlined
											className={`text-gray-500 transition-transform duration-300 text-lg ${isActive ? 'rotate-180' : ''}`}
										/>
									)}
									expandIconPosition="end"
									defaultActiveKey={['1', '2', '3']}
								>
									{callInformation?.callSummary && (
										<Collapse.Panel
											data-testid="summary-panel"
											key="1"
											header={
												<div className="flex items-center py-2">
													<FileTextOutlined className="text-blue-500 mr-2 text-xl" />
													<Text strong className="text-base">
														Call Summary
													</Text>
												</div>
											}
											className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 mb-4 overflow-hidden border-0"
											style={{
												boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
												marginBottom: '1.5rem',
											}}
										>
											<CallSummaryCard
												callSummary={callInformation?.callSummary}
												style={{ maxHeight: '500px', overflowY: 'auto' }}
											/>
										</Collapse.Panel>
									)}

									<Collapse.Panel
										data-testid="transcript-panel"
										key="2"
										header={
											<div className="flex items-center py-2">
												<MessageOutlined className="text-green-500 mr-2 text-xl" />
												<Text strong className="text-base">
													Call Transcript
												</Text>
											</div>
										}
										className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 mb-4 overflow-hidden border-0"
										style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', marginBottom: '1.5rem' }}
									>
										<CallTranscriptCard
											transcriptMessages={transcriptMessages}
											style={{ maxHeight: '500px', overflowY: 'auto' }}
										/>
									</Collapse.Panel>

									<Collapse.Panel
										data-testid="intake-form-panel"
										key="3"
										header={
											<div className="flex items-center py-2">
												<FormOutlined className="text-purple-500 mr-2 text-xl" />
												<Text strong className="text-base">
													Intake Form
												</Text>
											</div>
										}
										className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-0"
										style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
									>
										{ticketsLoading ? (
											<div className="flex justify-center p-4">
												<Spin />
											</div>
										) : (
											<CallTicketsCard
												tickets={tickets}
												style={{ maxHeight: '500px', overflowY: 'auto' }}
											/>
										)}
									</Collapse.Panel>
								</Collapse>
							</div>
						</div>
					) : (
						<Card
							data-testid="empty-state"
							className="text-center shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl border-0"
							style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
						>
							<div className="flex flex-col items-center gap-4 py-8">
								<PhoneOutlined className="text-4xl text-gray-300" />
								<Title level={4} className="!mb-0">
									No call information found
								</Title>
							</div>
						</Card>
					)}
				</div>
			</Content>
		</Layout>
	);
};

export default CallInformation;
